import {
  Box,
  Text,
  SimpleGrid,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
  Heading,
  useDisclosure,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Image,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  DeletePhaseModal,
  DocumentsList,
  TeamList,
  WorkflowsList,
} from '../components';

import { formatDate } from '../../../utils';
import { phaseDetailsTabs } from '../utils';
import {
  useDeletePhaseMutation,
  useGetPhaseByIdQuery,
} from '../../../services';
import { CenteredSpinner } from '../../../components';

import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';
import RedTrashImage from '../../../assets/icons/red-trash.svg';
import EditIconImage from '../../../assets/icons/edit.svg';

export const PhaseDetails = () => {
  const navigate = useNavigate();
  const { tabName, projectId, phaseId } = useParams();

  const { data, isLoading } = useGetPhaseByIdQuery({
    projectId: Number(projectId),
    phaseId: Number(phaseId),
  });

  const [deletePhase] = useDeletePhaseMutation();

  const {
    isOpen: isDeletePhaseModalOpen,
    onOpen: onOpenDeletePhaseModal,
    onClose: onCloseDeletePhaseModal,
  } = useDisclosure();

  let index = phaseDetailsTabs.findIndex(({ link }) => link === tabName);
  index = index === -1 ? 0 : index;

  if (isLoading || !data) return <CenteredSpinner />;

  const { progress, phaseEstimatedEndDate, phaseStartDate, phaseName } =
    data.data;

  return (
    <Box mt="8">
      {isDeletePhaseModalOpen && (
        <DeletePhaseModal
          isOpen={isDeletePhaseModalOpen}
          onClose={isDeleted => {
            if (isDeleted)
              void deletePhase({
                phaseId: Number(phaseId),
                projectId: Number(projectId),
              });
            onCloseDeletePhaseModal();
          }}
        />
      )}

      <HStack justify="space-between">
        <Heading fontSize="xl" fontWeight="bold">
          {phaseName}
        </Heading>

        <Menu closeOnSelect={false}>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                fontSize="sm"
                bg="none"
                _hover={{ bg: 'none' }}
                border="1px"
                borderColor="custom_gray.1"
              >
                Actions
              </MenuButton>
              <MenuList p="0">
                <MenuItem
                  border="1px"
                  borderColor="custom_gray.4"
                  p="3"
                  onClick={() => {
                    navigate(
                      `/projects/multiple-phase/${String(
                        projectId
                      )}/project-details/phases/update/${String(phaseId)}`
                    );
                  }}
                >
                  <HStack>
                    <Image src={EditIconImage} alt="Edit phase" />
                    <Text
                      color="custom_blue.1"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Edit Phase
                    </Text>
                  </HStack>
                </MenuItem>

                {/* TODO: reuse after implementation */}
                {/* <MenuItem border="1px" borderColor="custom_gray.4" p="3">
                  <HStack>
                    <Image src={EditIconImage} alt="Update phase progress" />
                    <Text
                      color="custom_blue.1"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Update Phase Progress
                    </Text>
                  </HStack>
                </MenuItem> */}

                <MenuItem
                  border="1px"
                  borderColor="custom_gray.4"
                  p="3"
                  onClick={onOpenDeletePhaseModal}
                >
                  <HStack>
                    <Image src={RedTrashImage} alt="Delete phase" />
                    <Text color="error.600" fontSize="sm" fontWeight="medium">
                      Delete Phase
                    </Text>
                  </HStack>
                </MenuItem>
              </MenuList>
            </>
          )}
        </Menu>
      </HStack>

      <Tabs
        position="relative"
        variant="unstyled"
        colorScheme="orange"
        index={index}
        onChange={tabIndex =>
          navigate(
            `/projects/multiple-phase/${String(
              projectId
            )}/project-details/phases/${String(phaseId)}/${
              phaseDetailsTabs[tabIndex].link
            }`
          )
        }
      >
        <TabList>
          {phaseDetailsTabs.map(({ name }) => (
            <Tab
              key={name}
              fontSize="sm"
              fontWeight="semibold"
              color="custom_gray.1"
              _selected={{ color: 'custom_orange.2' }}
            >
              {name}
            </Tab>
          ))}
        </TabList>
        <TabIndicator
          mt="-1.5px"
          height="2px"
          bg="custom_orange.2"
          borderRadius="1px"
        />

        <TabPanels
          borderRadius="md"
          boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
          border="1px"
          borderColor="custom_gray.3"
          background="white"
        >
          <TabPanel>
            <VStack align="stretch" gap="4">
              <SimpleGrid columns={2} spacing={4}>
                <Box
                  borderRadius="md"
                  boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                  border="1px"
                  borderColor="custom_gray.3"
                  p="4"
                >
                  <VStack align="flex-start">
                    <Text
                      color="custom_blue.1"
                      fontSize="md"
                      fontWeight="bold"
                      mb="6"
                    >
                      Start Date
                    </Text>
                    <Text color="custom_blue.1" fontSize="md">
                      {formatDate(phaseStartDate)}
                    </Text>
                  </VStack>
                </Box>

                <Box
                  borderRadius="md"
                  boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                  border="1px"
                  borderColor="custom_gray.3"
                  p="4"
                >
                  <VStack align="flex-start">
                    <Text
                      color="custom_blue.1"
                      fontSize="md"
                      fontWeight="bold"
                      mb="6"
                    >
                      End Date
                    </Text>
                    <Text color="custom_blue.1" fontSize="md">
                      {formatDate(phaseEstimatedEndDate)}
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>

              {/* TODO: reuse after implementation */}
              {/* <Box
                borderRadius="md"
                boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                border="1px"
                borderColor="custom_gray.3"
                p="4"
              >
                <VStack align="flex-start">
                  <Text
                    color="custom_blue.1"
                    fontSize="md"
                    fontWeight="bold"
                    mb="4"
                  >
                    Progress
                  </Text>
                  <CustomProgress value={progress} />
                </VStack>
              </Box> */}
            </VStack>
          </TabPanel>
          <TabPanel>
            <TeamList phaseId={String(phaseId)} />
          </TabPanel>
          <TabPanel>
            <DocumentsList phaseId={String(phaseId)} />
          </TabPanel>
          <TabPanel>COMING SOON 🚀</TabPanel>
          <TabPanel>COMING SOON 🚀</TabPanel>
          {/* TODO: reuse after implementation */}
          {/* <TabPanel>COMING SOON 🚀</TabPanel> */}
          <TabPanel>
            <WorkflowsList phaseId={String(phaseId)} />
          </TabPanel>
          {/* <TabPanel>COMING SOON 🚀</TabPanel> */}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
